import { useEffect, useRef, useCallback } from "react";
import { useAutoUpdateRef, useCanvasId } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import { useTypedSelector } from "@/store/store";
import {
  type DesignTokenApiResponse,
  useDesignTokensStore,
} from "../store/designTokensStore";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { useIframeStore } from "@/components/Canvas/stores/iframeStore";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { IFRAME_STATE, resetCssCustomProperties } from "@/store/iframeSlice";
import { useTailwindConfigStore } from "@/store/zustand/tailwindConfigStore";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { UpdateDesignTokensChange } from "@/store/zustand/changeLedgerTypes";
import { handleApiError } from "../utils/designTokensApi";
import { generateDesignTokenClasses } from "../utils/tailwindClassGeneration";
import {
  buildTokenPath,
  isUngroupedColor,
  parseColorName,
} from "../utils/tokenPathHelpers";
import {
  stripHexPrefix,
  normalizeColorValue,
} from "@/components/RightStyle/TailwindStylingPanel/components/input/color-picker/utils/colorFormatUtils";
import {
  validateColorName,
  validateColorValue,
  validateGroupName,
  validateAndReport,
} from "../utils/validation";
import { createColorToken } from "../utils/colorUtils";
import { removeUngroupedColor } from "../utils/optimisticUpdates";
import {
  generateSemanticDarkModeColor,
  generateSemanticLightModeColor,
} from "@/utils/darkModeColorGenerator";
import { toast } from "sonner";
import type { ColorGroup, ColorToken } from "../types";
import type { ClassTypeWithPropertyType } from "@/tailwind/tailwindUtils";
import { useDispatch } from "react-redux";
import store from "@/store/store";

export interface ColorToSave {
  name: string;
  group: string | null;
  value: string;
  isDark: boolean;
}

interface UseDesignTokensResult {
  addColor: (groupName: string, name: string, color: string) => Promise<void>;
  createColorWithDarkMode: (
    groupName: string,
    name: string,
    lightColor: string,
    darkColor: string,
  ) => Promise<void>;
  updateColor: (
    groupName: string,
    originalColorName: string,
    name?: string,
    color?: string,
    isDark?: boolean,
  ) => Promise<void>;
  deleteColor: (groupName: string, colorName: string) => Promise<void>;
  deleteColorGroup: (groupName: string) => Promise<void>;
  renameGroup: (oldName: string, newName: string) => Promise<void>;
  hasAnyColors: boolean;
  refreshColors: () => Promise<void>;
  syncFromApiResponse: (response: DesignTokenApiResponse) => void;
  saveColorTokens: (colors: ColorToSave[]) => Promise<void>;
}

export function useDesignTokens(): UseDesignTokensResult {
  const canvasId = useCanvasId();
  const session = useSession();
  const isCanvasDarkMode = useDarkModeStore((state) => state.isCanvasDarkMode);

  const lightColors = useDesignTokensStore((state) => state.lightColors);
  const lightColorGroups = useDesignTokensStore(
    (state) => state.lightColorGroups,
  );
  const darkColors = useDesignTokensStore((state) => state.darkColors);
  const darkColorGroups = useDesignTokensStore(
    (state) => state.darkColorGroups,
  );
  const hasDarkModeColors = useDesignTokensStore(
    (state) => state.hasDarkModeColors,
  );
  const initializedCanvasId = useDesignTokensStore(
    (state) => state.initializedCanvasId,
  );
  const lastRawApiResponse = useDesignTokensStore(
    (state) => state.lastRawApiResponse,
  );

  const {
    setError,
    setInitializedCanvasId,
    fetchColors,
    syncFromApiResponse,
    updateMergedTokens,
    updateTailwindClasses,
    processApiResponseWithCss,
    setColors,
    setColorGroups,
    setDarkColors,
    setDarkColorGroups,
  } = useDesignTokensStore();

  const cssCustomProperties = useTypedSelector(
    (state) => state.iframe.cssCustomProperties,
  );
  const cssCustomPropertiesRef = useAutoUpdateRef(cssCustomProperties);

  const devServerStatus = useTypedSelector(
    (state) => state.codebase.devServerStatus,
  );
  const serverDown = useTypedSelector((state) => state.codebase.serverDown);

  const tailwindConfigChangedTrigger = useTailwindConfigStore(
    (state) => state.tailwindConfigChangedTrigger,
  );

  const applicationStoryboards = useIframePoolStore(
    (state) => state.applicationStoryboards,
  );

  // There must always be at least one application storyboard
  const applicationStoryboard = Object.values(applicationStoryboards)[0];

  const storyboardIdToExtractFrom = applicationStoryboard?.storyboard?.id;

  // Refs for tracking state
  const prevServerDownRef = useRef(serverDown);
  const prevDevServerStatusRef = useRef(devServerStatus);

  const dispatch = useDispatch();

  // reset state on canvas change
  useEffect(() => {
    dispatch(resetCssCustomProperties());
    setInitializedCanvasId(null);
  }, [canvasId, dispatch, setInitializedCanvasId]);

  // Request CSS custom properties from iframe when ready
  useEffect(() => {
    if (!cssCustomPropertiesRef.current && storyboardIdToExtractFrom) {
      const interval = setInterval(() => {
        if (cssCustomPropertiesRef.current) {
          clearInterval(interval);
          return;
        }

        useIframePoolStore
          .getState()
          .sendMessageToApplicationStoryboard(
            storyboardIdToExtractFrom,
            "executeFunction",
            {
              functionName: "extractCssCustomProperties",
              args: [],
            },
          );
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [storyboardIdToExtractFrom]);

  // Fetch colors once when canvas loads
  useEffect(() => {
    if (
      !canvasId ||
      initializedCanvasId === canvasId ||
      devServerStatus?.isServerRunning === false
    ) {
      return;
    }

    setInitializedCanvasId(canvasId);
    if (session?.session) {
      fetchColors(canvasId, session.session, cssCustomProperties, true);
    }
  }, [
    canvasId,
    initializedCanvasId,
    Boolean(session),
    setInitializedCanvasId,
    fetchColors,
    devServerStatus?.isServerRunning,
    cssCustomProperties,
  ]);

  useEffect(() => {
    if (!cssCustomProperties) {
      return;
    }
    useDesignTokensStore
      .getState()
      .syncFromCssCustomProperties(cssCustomProperties);
  }, [cssCustomProperties]);

  // Reprocess API response when CSS properties become available
  useEffect(() => {
    if (!cssCustomPropertiesRef.current || !lastRawApiResponse) {
      return;
    }
    processApiResponseWithCss(
      lastRawApiResponse,
      isCanvasDarkMode,
      cssCustomPropertiesRef.current,
    );
  }, [lastRawApiResponse, isCanvasDarkMode, processApiResponseWithCss]);

  // Update Tailwind classes when tokens change
  useEffect(() => {
    updateTailwindClasses();
  }, [
    lightColorGroups,
    lightColors,
    darkColorGroups,
    darkColors,
    updateTailwindClasses,
  ]);

  // Handle server restart
  useEffect(() => {
    if (
      prevServerDownRef.current === true &&
      serverDown === false &&
      canvasId &&
      session?.session
    ) {
      setTimeout(() => {
        if (cssCustomPropertiesRef.current !== null) {
          fetchColors(
            canvasId,
            session.session,
            cssCustomPropertiesRef.current,
            false,
          );
        }
      }, 1000);
    }
    prevServerDownRef.current = serverDown;
  }, [serverDown, canvasId, Boolean(session), fetchColors]);

  // Handle dev server restart
  useEffect(() => {
    if (
      prevDevServerStatusRef.current?.isServerRunning === false &&
      devServerStatus?.isServerRunning === true &&
      canvasId
    ) {
      setColors([]);
      setColorGroups([]);
      setDarkColors([]);
      setDarkColorGroups([]);
      setError(null);
    }
    prevDevServerStatusRef.current = devServerStatus;
  }, [
    devServerStatus,
    canvasId,
    setColors,
    setColorGroups,
    setDarkColors,
    setDarkColorGroups,
    setError,
  ]);

  // Listen for external Tailwind config changes
  useEffect(() => {
    if (tailwindConfigChangedTrigger > 0 && canvasId && session?.session) {
      fetchColors(
        canvasId,
        session.session,
        cssCustomPropertiesRef.current,
        false,
      );
    }
  }, [tailwindConfigChangedTrigger, canvasId, Boolean(session)]);

  // Update merged tokens when dark mode changes
  useEffect(() => {
    updateMergedTokens(isCanvasDarkMode);
  }, [isCanvasDarkMode, updateMergedTokens]);

  // Create and execute design token changes through the change ledger
  const createAndExecuteTokenChange = useCallback(
    (
      apiPayload: any,
      changedTokenPaths: string[],
      operationName: string,
      operationType: "create" | "update" | "delete",
      previousValues?: Record<string, string>,
    ) => {
      const affectedTokenClasses: ClassTypeWithPropertyType[] = [];

      const getCurrentColorFromState = (path: string): string | undefined => {
        const isDarkPath = path.startsWith("dark-");
        const cleanPath = isDarkPath ? path.substring(5) : path;
        const targetGroups = isDarkPath ? darkColorGroups : lightColorGroups;
        const targetUngroupedColors = isDarkPath ? darkColors : lightColors;

        if (!cleanPath.includes("/")) {
          const color = targetUngroupedColors.find((c) => c.name === cleanPath);
          return color?.color;
        }

        const { groupName, colorName } = parseColorName(cleanPath);
        const group = targetGroups.find((g) => g.name === groupName);
        if (group) {
          const color = group.colors.find((c) => c.name === colorName);
          return color?.color;
        }
        return undefined;
      };

      const getColorValueForPath = (path: string): string | undefined => {
        if (operationType === "delete") {
          return getCurrentColorFromState(path);
        }

        if (apiPayload.created) {
          for (const item of apiPayload.created) {
            const fullPath = buildTokenPath(
              item.groupName,
              item.colorName,
              item.isDark,
            );
            if (fullPath === path) {
              return item.value.startsWith("#") ? item.value : `#${item.value}`;
            }
          }
        }

        if (apiPayload.updated) {
          for (const [updatePath, updateData] of Object.entries(
            apiPayload.updated as Record<string, any>,
          )) {
            if (updatePath === path && updateData.value) {
              const value = updateData.value as string;
              return value.startsWith("#") ? value : `#${value}`;
            }
            if (updateData.newPath === path && updateData.value) {
              const value = updateData.value as string;
              return value.startsWith("#") ? value : `#${value}`;
            }
          }
        }

        return undefined;
      };

      for (const tokenPath of changedTokenPaths) {
        const tokenColor = getColorValueForPath(tokenPath);
        if (tokenColor) {
          const tokenClasses = generateDesignTokenClasses(
            tokenPath,
            tokenColor,
          );
          affectedTokenClasses.push(...tokenClasses);
        }
      }

      let deletedTokenClasses:
        | { tailwind: string; variant?: string | null }[]
        | undefined;
      let previousTokenClasses:
        | { tailwind: string; css: string; variant?: string | null }[]
        | undefined;

      if (operationType === "delete") {
        deletedTokenClasses = affectedTokenClasses.map((cls) => ({
          tailwind: cls.tailwind,
          variant: null,
        }));
        previousTokenClasses = affectedTokenClasses.map((cls) => ({
          tailwind: cls.tailwind,
          css: cls.css,
          variant: null,
        }));
      } else if (operationType === "update") {
        const previousColors: {
          tailwind: string;
          css: string;
          variant?: string | null;
        }[] = [];
        for (const tokenPath of changedTokenPaths) {
          const oldColor =
            previousValues?.[tokenPath] || getCurrentColorFromState(tokenPath);
          if (oldColor) {
            const oldTokenClasses = generateDesignTokenClasses(
              tokenPath,
              oldColor,
            );
            previousColors.push(
              ...oldTokenClasses.map((cls) => ({
                tailwind: cls.tailwind,
                css: cls.css,
                variant: null,
              })),
            );
          }
        }
        if (previousColors.length > 0) {
          previousTokenClasses = previousColors;
        }
      }

      const tokenChange = new UpdateDesignTokensChange({
        updatedTokenClasses:
          operationType !== "delete"
            ? affectedTokenClasses.map((cls) => ({
                tailwind: cls.tailwind,
                css: cls.css,
                variant: null,
              }))
            : [],
        deletedTokenClasses,
        previousTokenClasses,
        changedTokenPaths,
        operationType,
        apiPayload,
        operationName,
      });

      tokenChange.onApiResolve((response) => {
        if (response?.colors) {
          syncFromApiResponse(response, isCanvasDarkMode);
        }
      });

      useDesignCommandManager.getState().executeCommand(tokenChange);
    },
    [
      syncFromApiResponse,
      isCanvasDarkMode,
      lightColorGroups,
      lightColors,
      darkColorGroups,
      darkColors,
    ],
  );

  const refreshColors = useCallback(async () => {
    if (!canvasId || !session?.session) return;
    await fetchColors(
      canvasId,
      session.session,
      cssCustomPropertiesRef.current,
      true,
    );
  }, [canvasId, Boolean(session), fetchColors]);

  const addColor = useCallback(
    async (groupName: string, name: string, color: string) => {
      if (!canvasId || !session?.session) {
        toast.warning("Please wait for current operation to complete");
        return;
      }

      if (
        !validateAndReport(
          validateColorName(name),
          "color name",
          setError,
          (msg, desc) => toast.error(msg, { description: desc }),
        ) ||
        !validateAndReport(
          validateColorValue(color),
          "color value",
          setError,
          (msg, desc) => toast.error(msg, { description: desc }),
        )
      ) {
        return;
      }

      const isUngrouped = isUngroupedColor(groupName, name);
      if (
        !isUngrouped &&
        !validateAndReport(
          validateGroupName(groupName),
          "group name",
          setError,
          (msg, desc) => toast.error(msg, { description: desc }),
        )
      ) {
        return;
      }

      // Optimistic update
      const newColor = createColorToken(
        name,
        color,
        cssCustomPropertiesRef.current?.root,
      );
      const updatedGroups = [...lightColorGroups];
      const updatedUngroupedColors = [...lightColors];
      const updatedDarkGroups = [...darkColorGroups];
      const updatedDarkUngroupedColors = [...darkColors];

      if (hasDarkModeColors) {
        const oppositeColor = isCanvasDarkMode
          ? generateSemanticLightModeColor(name, color)
          : generateSemanticDarkModeColor(name, color);
        const newOppositeColor = createColorToken(
          name,
          oppositeColor,
          isCanvasDarkMode
            ? cssCustomPropertiesRef.current?.root
            : cssCustomPropertiesRef.current?.dark,
        );

        if (isCanvasDarkMode) {
          if (isUngrouped) {
            updatedUngroupedColors.push(newOppositeColor);
          } else {
            const existingGroupIndex = updatedGroups.findIndex(
              (g) => g.name === groupName,
            );
            if (existingGroupIndex >= 0) {
              updatedGroups[existingGroupIndex] = {
                ...updatedGroups[existingGroupIndex],
                colors: [
                  ...updatedGroups[existingGroupIndex].colors,
                  newOppositeColor,
                ],
              };
            } else {
              updatedGroups.push({
                name: groupName,
                colors: [newOppositeColor],
                isExpanded: true,
              });
            }
          }
        } else {
          if (isUngrouped) {
            updatedDarkUngroupedColors.push(newOppositeColor);
          } else {
            const existingGroupIndex = updatedDarkGroups.findIndex(
              (g) => g.name === groupName,
            );
            if (existingGroupIndex >= 0) {
              updatedDarkGroups[existingGroupIndex] = {
                ...updatedDarkGroups[existingGroupIndex],
                colors: [
                  ...updatedDarkGroups[existingGroupIndex].colors,
                  newOppositeColor,
                ],
              };
            } else {
              updatedDarkGroups.push({
                name: groupName,
                colors: [newOppositeColor],
                isExpanded: true,
              });
            }
          }
        }
      }

      if (isCanvasDarkMode) {
        if (isUngrouped) {
          updatedDarkUngroupedColors.push(newColor);
        } else {
          const existingGroupIndex = updatedDarkGroups.findIndex(
            (g) => g.name === groupName,
          );
          if (existingGroupIndex >= 0) {
            updatedDarkGroups[existingGroupIndex] = {
              ...updatedDarkGroups[existingGroupIndex],
              colors: [
                ...updatedDarkGroups[existingGroupIndex].colors,
                newColor,
              ],
            };
          } else {
            updatedDarkGroups.push({
              name: groupName,
              colors: [newColor],
              isExpanded: true,
            });
          }
        }
      } else {
        if (isUngrouped) {
          updatedUngroupedColors.push(newColor);
        } else {
          const existingGroupIndex = updatedGroups.findIndex(
            (g) => g.name === groupName,
          );
          if (existingGroupIndex >= 0) {
            updatedGroups[existingGroupIndex] = {
              ...updatedGroups[existingGroupIndex],
              colors: [...updatedGroups[existingGroupIndex].colors, newColor],
            };
          } else {
            updatedGroups.push({
              name: groupName,
              colors: [newColor],
              isExpanded: true,
            });
          }
        }
      }

      setColorGroups(updatedGroups);
      setColors(updatedUngroupedColors);
      setDarkColorGroups(updatedDarkGroups);
      setDarkColors(updatedDarkUngroupedColors);

      const createdColors = [
        {
          groupName,
          colorName: name,
          value: stripHexPrefix(color),
          isDark: isCanvasDarkMode,
        },
      ];

      if (hasDarkModeColors) {
        const oppositeColor = isCanvasDarkMode
          ? generateSemanticLightModeColor(name, color)
          : generateSemanticDarkModeColor(name, color);
        createdColors.push({
          groupName,
          colorName: name,
          value: stripHexPrefix(oppositeColor),
          isDark: !isCanvasDarkMode,
        });
      }

      const changedTokenPaths = [
        buildTokenPath(groupName, name, isCanvasDarkMode),
      ];
      if (hasDarkModeColors) {
        changedTokenPaths.push(
          buildTokenPath(groupName, name, !isCanvasDarkMode),
        );
      }

      createAndExecuteTokenChange(
        { created: createdColors },
        changedTokenPaths,
        `Add ${groupName ? "grouped" : "ungrouped"} color "${name}"`,
        "create",
      );
    },
    [
      canvasId,
      Boolean(session),
      lightColorGroups,
      lightColors,
      darkColorGroups,
      darkColors,
      hasDarkModeColors,
      isCanvasDarkMode,
      setColorGroups,
      setColors,
      setDarkColorGroups,
      setDarkColors,
      setError,
      createAndExecuteTokenChange,
    ],
  );

  const createColorWithDarkMode = useCallback(
    async (
      groupName: string,
      name: string,
      lightColor: string,
      darkColor: string,
    ) => {
      if (!canvasId || !session?.session) {
        toast.warning("Please wait for current operation to complete");
        return;
      }

      if (
        !validateAndReport(
          validateColorName(name),
          "color name",
          setError,
          (msg, desc) => toast.error(msg, { description: desc }),
        ) ||
        !validateAndReport(
          validateColorValue(lightColor),
          "light color value",
          setError,
          (msg, desc) => toast.error(msg, { description: desc }),
        ) ||
        !validateAndReport(
          validateColorValue(darkColor),
          "dark color value",
          setError,
          (msg, desc) => toast.error(msg, { description: desc }),
        )
      ) {
        return;
      }

      const isUngrouped = isUngroupedColor(groupName, name);
      if (
        !isUngrouped &&
        !validateAndReport(
          validateGroupName(groupName),
          "group name",
          setError,
          (msg, desc) => toast.error(msg, { description: desc }),
        )
      ) {
        return;
      }

      const createdColors = [
        {
          groupName,
          colorName: name,
          value: stripHexPrefix(lightColor),
          isDark: false,
        },
        {
          groupName,
          colorName: name,
          value: stripHexPrefix(darkColor),
          isDark: true,
        },
      ];

      const changedTokenPaths = [
        buildTokenPath(groupName, name, false),
        buildTokenPath(groupName, name, true),
      ];

      createAndExecuteTokenChange(
        { created: createdColors },
        changedTokenPaths,
        `Create color "${name}" with dark mode`,
        "create",
      );
    },
    [canvasId, Boolean(session), setError, createAndExecuteTokenChange],
  );

  const updateColor = useCallback(
    async (
      groupName: string,
      originalColorName: string,
      name?: string,
      color?: string,
      isDark?: boolean,
    ) => {
      if (!canvasId || !session?.session) {
        toast.warning("Please wait for current operation to complete");
        return;
      }

      if (
        (name &&
          name !== originalColorName &&
          !validateAndReport(
            validateColorName(name),
            "color name",
            setError,
            (msg, desc) => toast.error(msg, { description: desc }),
          )) ||
        (color &&
          !validateAndReport(
            validateColorValue(color),
            "color value",
            setError,
            (msg, desc) => toast.error(msg, { description: desc }),
          ))
      ) {
        return;
      }

      const isCurrentlyUngrouped = groupName === originalColorName;
      const currentColorPath = isCurrentlyUngrouped
        ? originalColorName
        : `${groupName}/${originalColorName}`;
      let newColorPath = currentColorPath;

      if (name && name !== originalColorName) {
        const parts = name.split("/");
        if (parts.length === 2) {
          newColorPath = name;
        } else if (isCurrentlyUngrouped) {
          newColorPath = name;
        } else {
          newColorPath = `${groupName}/${name}`;
        }
      }

      // Fast path for color value only update
      if ((!name || name === originalColorName) && color) {
        const normalizedValue = normalizeColorValue(color);
        if (isDark) {
          if (groupName === originalColorName) {
            setDarkColors(
              darkColors.map((c) =>
                c.name === originalColorName
                  ? { ...c, color: normalizedValue, value: normalizedValue }
                  : c,
              ),
            );
          } else {
            setDarkColorGroups(
              darkColorGroups.map((g) =>
                g.name === groupName
                  ? {
                      ...g,
                      colors: g.colors.map((c) =>
                        c.name === originalColorName
                          ? {
                              ...c,
                              color: normalizedValue,
                              value: normalizedValue,
                            }
                          : c,
                      ),
                    }
                  : g,
              ),
            );
          }
        } else {
          if (groupName === originalColorName) {
            setColors(
              lightColors.map((c) =>
                c.name === originalColorName
                  ? { ...c, color: normalizedValue, value: normalizedValue }
                  : c,
              ),
            );
          } else {
            setColorGroups(
              lightColorGroups.map((g) =>
                g.name === groupName
                  ? {
                      ...g,
                      colors: g.colors.map((c) =>
                        c.name === originalColorName
                          ? {
                              ...c,
                              color: normalizedValue,
                              value: normalizedValue,
                            }
                          : c,
                      ),
                    }
                  : g,
              ),
            );
          }
        }
      }

      const requestPayload = {
        updated: {
          [currentColorPath]: {
            ...(newColorPath !== currentColorPath && { newPath: newColorPath }),
            ...(color && { value: stripHexPrefix(color) }),
            ...(isDark !== undefined && { isDark }),
            // Include group and color name information when path changes
            ...(newColorPath !== currentColorPath &&
              (() => {
                const { groupName: newGroupName, colorName: newColorName } =
                  parseColorName(newColorPath);
                return {
                  groupName: newGroupName,
                  colorName: newColorName,
                };
              })()),
          },
        },
      };

      const changedTokenPaths =
        newColorPath !== currentColorPath
          ? [currentColorPath, newColorPath]
          : [currentColorPath];

      createAndExecuteTokenChange(
        requestPayload,
        changedTokenPaths,
        isUngroupedColor(groupName, originalColorName)
          ? `Update color "${originalColorName}"`
          : `Update color "${originalColorName}" in "${groupName}"`,
        "update",
      );
    },
    [
      canvasId,
      Boolean(session),
      lightColors,
      lightColorGroups,
      darkColors,
      darkColorGroups,
      setColors,
      setColorGroups,
      setDarkColors,
      setDarkColorGroups,
      setError,
      createAndExecuteTokenChange,
    ],
  );

  const deleteColor = useCallback(
    async (groupName: string, colorName: string) => {
      if (!canvasId || !session?.session) {
        toast.warning("Please wait for current operation to complete");
        return;
      }

      const updatedGroups = groupName
        ? lightColorGroups.map((group) =>
            group.name === groupName
              ? {
                  ...group,
                  colors: group.colors.filter((c) => c.name !== colorName),
                }
              : group,
          )
        : lightColorGroups;
      const updatedUngroupedColors = !groupName
        ? removeUngroupedColor(lightColors, colorName)
        : lightColors;

      setColorGroups(updatedGroups);
      setColors(updatedUngroupedColors);

      const colorPath = groupName ? `${groupName}/${colorName}` : colorName;
      createAndExecuteTokenChange(
        { deleted: [colorPath] },
        [colorPath],
        `Delete color "${colorName}"`,
        "delete",
      );
    },
    [
      canvasId,
      Boolean(session),
      lightColorGroups,
      lightColors,
      setColorGroups,
      setColors,
      createAndExecuteTokenChange,
    ],
  );

  const deleteColorGroup = useCallback(
    async (groupName: string) => {
      if (!canvasId || !session?.session) {
        toast.warning("Please wait for current operation to complete");
        return;
      }

      const lightGroupToDelete = lightColorGroups.find(
        (group) => group.name === groupName,
      );
      const darkGroupToDelete = darkColorGroups.find(
        (group) => group.name === groupName,
      );

      if (!lightGroupToDelete && !darkGroupToDelete) return;

      const updatedGroups = lightColorGroups.filter(
        (group) => group.name !== groupName,
      );
      const updatedDarkGroups = darkColorGroups.filter(
        (group) => group.name !== groupName,
      );

      setColorGroups(updatedGroups);
      setDarkColorGroups(updatedDarkGroups);

      const colorsToDelete: string[] = [];
      if (lightGroupToDelete) {
        colorsToDelete.push(
          ...lightGroupToDelete.colors.map(
            (color) => `${groupName}/${color.name}`,
          ),
        );
      }
      if (darkGroupToDelete) {
        colorsToDelete.push(
          ...darkGroupToDelete.colors.map(
            (color) => `${groupName}/${color.name}`,
          ),
        );
      }

      const uniqueColorsToDelete = [...new Set(colorsToDelete)];
      createAndExecuteTokenChange(
        { deleted: uniqueColorsToDelete },
        uniqueColorsToDelete,
        `Delete color group "${groupName}"`,
        "delete",
      );
    },
    [
      canvasId,
      Boolean(session),
      lightColorGroups,
      darkColorGroups,
      setColorGroups,
      setDarkColorGroups,
      createAndExecuteTokenChange,
    ],
  );

  const renameGroup = useCallback(
    async (oldName: string, newName: string) => {
      if (
        !canvasId ||
        !session?.session ||
        oldName === newName ||
        !newName.trim()
      ) {
        return;
      }

      const groupToRename = lightColorGroups.find(
        (group) => group.name === oldName,
      );
      if (!groupToRename) return;

      const updatedGroups = lightColorGroups.map((group) =>
        group.name === oldName ? { ...group, name: newName } : group,
      );
      setColorGroups(updatedGroups);

      const darkGroupToRename = darkColorGroups.find(
        (group) => group.name === oldName,
      );
      const colorsToDelete: string[] = [];
      const colorsToCreate: any[] = [];

      for (const color of groupToRename.colors) {
        colorsToDelete.push(buildTokenPath(oldName, color.name, false));
        colorsToCreate.push({
          groupName: newName,
          colorName: color.name,
          value: color.value,
          isDark: false,
        });
      }

      if (darkGroupToRename) {
        for (const color of darkGroupToRename.colors) {
          colorsToDelete.push(buildTokenPath(oldName, color.name, true));
          colorsToCreate.push({
            groupName: newName,
            colorName: color.name,
            value: color.value,
            isDark: true,
          });
        }
      }

      const changedTokenPaths = [...colorsToDelete];
      for (const color of colorsToCreate) {
        changedTokenPaths.push(
          buildTokenPath(color.groupName, color.colorName, color.isDark),
        );
      }

      createAndExecuteTokenChange(
        { deleted: colorsToDelete, created: colorsToCreate },
        changedTokenPaths,
        `Rename group "${oldName}" to "${newName}"`,
        "update",
      );
    },
    [
      canvasId,
      Boolean(session),
      lightColorGroups,
      darkColorGroups,
      setColorGroups,
      createAndExecuteTokenChange,
    ],
  );

  const saveColorTokens = useCallback(
    async (colors: ColorToSave[]) => {
      if (!canvasId || !session?.session) {
        throw new Error("No canvas ID or session available");
      }

      try {
        const createdColors = colors.map((color) => ({
          groupName: color.group || color.name,
          colorName: color.name,
          value: stripHexPrefix(color.value),
          isDark: color.isDark,
        }));

        const changedTokenPaths = colors.map((color) => {
          const groupName = color.group || color.name;
          return buildTokenPath(groupName, color.name, color.isDark);
        });

        createAndExecuteTokenChange(
          { created: createdColors },
          changedTokenPaths,
          `Save ${colors.length} color token${colors.length === 1 ? "" : "s"}`,
          "create",
        );
      } catch (error) {
        const errorMessage = handleApiError(error, "Failed to save colors");
        setError(errorMessage);
        throw error;
      }
    },
    [canvasId, Boolean(session), setError, createAndExecuteTokenChange],
  );

  const hasAnyColors =
    lightColors.length > 0 ||
    lightColorGroups.length > 0 ||
    darkColors.length > 0 ||
    darkColorGroups.length > 0;

  const syncFromApiResponseWrapper = useCallback(
    (response: any) => {
      syncFromApiResponse(response, isCanvasDarkMode);
    },
    [syncFromApiResponse, isCanvasDarkMode],
  );

  return {
    addColor,
    createColorWithDarkMode,
    updateColor,
    deleteColor,
    deleteColorGroup,
    renameGroup,
    hasAnyColors,
    refreshColors,
    syncFromApiResponse: syncFromApiResponseWrapper,
    saveColorTokens,
  };
}
